import {
  IShoppingCart,
  IShoppingCartProduct,
  IProduct,
  IFirebaseShoppingCartProducts,
  IOOSProduct,
  IReplacementProduct,
  ProductPricingFeatures,
} from '@monofront/types';
import {
  getIsGiftItem,
  getHasHiddenPrice,
  getIsFresh,
  getIsUltraFresh,
  getProductPricing,
} from '../../utils';

export const getShoppingCartProduct = (
  algoliaProduct: IProduct | IOOSProduct,
  quantity: number,
  features: ProductPricingFeatures,
  replacementProduct?: IReplacementProduct,
  hasPartialOOSProduct?: boolean,
): IShoppingCartProduct => {
  const productPricing = getProductPricing(algoliaProduct, quantity, features);

  const shoppingCartProduct: IShoppingCartProduct = {
    ...algoliaProduct,
    quantity,
    membershipPriceSubtotal: productPricing.price * quantity,
    hasPartialOOSProduct,
    replacementProduct,
  };

  return shoppingCartProduct;
};

export const computeShoppingCart = (
  algoliaProducts: Array<IProduct | IOOSProduct>,
  productsInCart: IFirebaseShoppingCartProducts,
  getProductKey: (product: IProduct | IOOSProduct) => string,
  shoppingCartId: string,
  features: ProductPricingFeatures,
): IShoppingCart => {
  const sortedAlgoliaProducts = algoliaProducts
    .slice()
    .sort((a, b) => a.sku.localeCompare(b.sku));
  const shoppingCart: IShoppingCart = sortedAlgoliaProducts.reduce(
    (
      accumulator: IShoppingCart,
      currentAlgoliaProduct: IProduct | IOOSProduct,
    ) => {
      const {
        price,
        compare_at_price: notMembershipPrice,
        grams,
        inventory_quantity: inventoryQuantity,
        meta,
      } = currentAlgoliaProduct;
      const quantityOfProductInCart =
        productsInCart[getProductKey(currentAlgoliaProduct)];
      const replacementProduct =
        (currentAlgoliaProduct as IOOSProduct).replacementProduct || null;
      const isFullAvailableProduct =
        inventoryQuantity >= quantityOfProductInCart;
      const isFullOOSProduct = Boolean(
        quantityOfProductInCart > 0 && inventoryQuantity <= 0,
      );
      const hasPartialOOSProduct = Boolean(
        quantityOfProductInCart > 0 &&
          inventoryQuantity > 0 &&
          replacementProduct,
      );
      const partialOOSQuantity =
        isFullOOSProduct || isFullAvailableProduct
          ? 0
          : quantityOfProductInCart - inventoryQuantity;
      const purchasableQuantity = isFullOOSProduct
        ? 0
        : quantityOfProductInCart - partialOOSQuantity;

      if (!quantityOfProductInCart) {
        return accumulator;
      }

      const shoppingCartProduct: IShoppingCartProduct = getShoppingCartProduct(
        currentAlgoliaProduct,
        isFullOOSProduct ? quantityOfProductInCart : purchasableQuantity,
        features,
        isFullOOSProduct ? replacementProduct : undefined,
        hasPartialOOSProduct,
      );

      const isProductGiftItem = getIsGiftItem(shoppingCartProduct);

      const giftItems = [...accumulator.giftItems];

      if (isProductGiftItem) {
        giftItems.push(shoppingCartProduct);
      }

      const pfandPrice = (meta?.finance?.pfand || 0) * purchasableQuantity;

      if (!isFullOOSProduct) {
        accumulator.products.push(shoppingCartProduct);
      } else if (!hasPartialOOSProduct) {
        accumulator.oosProducts.push(
          shoppingCartProduct as IShoppingCartProduct,
        );
      }

      if (hasPartialOOSProduct) {
        const shoppingCartPartialOOSProduct = getShoppingCartProduct(
          currentAlgoliaProduct,
          partialOOSQuantity,
          features,
          replacementProduct,
        );

        accumulator.oosProducts.push(
          shoppingCartPartialOOSProduct as IShoppingCartProduct,
        );
      }

      const productPrice =
        shoppingCartProduct.membershipPriceSubtotal <= 0
          ? 0
          : shoppingCartProduct.membershipPriceSubtotal;

      const totalPrice =
        accumulator.totalPrice + (isFullOOSProduct ? 0 : productPrice);
      const subtotal = accumulator.subtotal + price * purchasableQuantity;

      return {
        shoppingCartId,
        products: accumulator.products,
        oosProducts: accumulator.oosProducts,
        totalPrice,
        subtotal,
        promotion: totalPrice - subtotal,
        totalPfand: accumulator.totalPfand! + pfandPrice,
        totalQuantity: accumulator.totalQuantity + purchasableQuantity,
        totalWeight:
          accumulator.totalWeight + (grams / 1000) * purchasableQuantity,
        totalEconomy:
          notMembershipPrice > 0
            ? accumulator.totalEconomy +
              (notMembershipPrice - price) * purchasableQuantity
            : accumulator.totalEconomy,
        hasHiddenPrice:
          accumulator.hasHiddenPrice || getHasHiddenPrice(shoppingCartProduct),
        hasFreshProduct:
          accumulator.hasFreshProduct || getIsFresh(shoppingCartProduct.tags),
        hasUltraFreshProduct:
          accumulator.hasUltraFreshProduct ||
          getIsUltraFresh(shoppingCartProduct.tags),
        hasGiftItem: accumulator.hasGiftItem || isProductGiftItem,
        hasEarlyExpirationProduct:
          accumulator.hasEarlyExpirationProduct ||
          shoppingCartProduct.has_early_expiration,
        // hotfix old gift item bug: https://gitlab.com/la-fourche/tech/shopify-theme/-/issues/38
        giftItems,
      } as IShoppingCart;
    },
    {
      shoppingCartId,
      products: [],
      oosProducts: [],
      totalPrice: 0,
      totalPfand: 0,
      totalQuantity: 0,
      totalWeight: 0,
      totalEconomy: 0,
      hasHiddenPrice: false,
      hasFreshProduct: false,
      hasUltraFreshProduct: false,
      hasEarlyExpirationProduct: false,
      hasGiftItem: false,
      giftItems: [],
      subtotal: 0,
      promotion: 0,
    },
  );

  return shoppingCart;
};
